import { defineField, defineType } from 'sanity'

import ShowFilename from '../../components/ShowFilename'
import getPageInfo, { seoGroup } from '../objects/pageInfo'
import { PageBlockTypes } from '../pageBlocks'
import { strongEmLink } from '../../data/richText'

export default defineType({
  name: 'sku',
  title: 'SKUs',
  type: 'document',
  orderings: [
    {
      title: 'Name A-Z',
      name: 'nameAsc',
      by: [{field: 'name', direction: 'asc'}],
    },
  ],
  groups: [
    {
      name: 'content',
      title: 'Content',
      default: true,
    },
    {
      name: 'config',
      title: 'Config',
    },
    seoGroup,
  ],
  fields: [
    defineField({
      title: 'Order',
      name: 'order',
      type: 'number',
      group: 'config',
    }),
    defineField({
      name: 'name',
      title: 'Name',
      type: 'string',
      validation: (rule) => rule.required(),
      group: 'content',
    }),
    defineField({
      name: 'description',
      title: 'Description',
      type: 'array',
      of: strongEmLink,
      group: 'content',
    }),
    defineField({
      name: 'sku',
      title: 'SKU',
      type: 'string',
      description:
        'This has to match pages/accessories/accessory/configuration/sku.',
      validation: (rule) => rule.required(),
      group: 'content',
    }),
    defineField({
      name: 'simple',
      title: 'Accessory Configurator?',
      type: 'boolean',
      description:
        'If turned on, this will not be available under /shop/:id url.  It will be used to pull correct images for all accessories under /configure/:id/accessories pages.',
      group: 'content',
    }),
    defineField({
      name: 'accessoryPage',
      title: 'Accessory Page',
      type: 'reference',
      to: [
        {
          type: 'accessory',
        },
      ],
      validation: (rule) =>
        rule.custom((accessoryPage, context) => {
          const isSimple = context.parent?.simple
          if (!isSimple && !accessoryPage) return 'Accessory Page is required'
          return true
        }),
      hidden: ({ parent }) => parent?.simple,
      description:
        'This should not be set if Accessory Configurator option above is enabled.',
      group: 'content',
    }),
    defineField({
      title: 'Starting Price Unit of Measure',
      name: 'unitOfMeasure',
      type: 'string',
      description: 'Defaults to "ea."',
      initialValue: 'ea.',
      group: 'content',
    }),
    defineField({
      title: 'Options',
      name: 'options',
      type: 'array',
      of: [{ type: 'string' }],
      options: {
        list: [{ title: 'Show Bolt Pattern', value: 'showBoltPattern' }],
      },
      hidden: ({ parent }) => parent?.simple,
      group: 'content',
    }),
    defineField({
      name: 'photo',
      title: 'Photo',
      type: 'image',
      options: {
        hotspot: true,
      },
      group: 'content',
    }),
    defineField({
      title: 'Finish Photos',
      name: 'finishPhotos',
      type: 'array',
      of: [
        {
          type: 'image',
          options: {
            hotspot: true,
          },
          preview: {
            select: {
              finish: 'finish',
              media: 'asset',
            },
            prepare({ finish = '', media }) {
              const title = finish.length === 0 ? 'Click to Complete' : finish
              return { title, media }
            },
          },
          fields: [
            defineField({
              name: 'originalfn',
              title: 'Original Filename',
              type: 'string',
              options: { isHighlighted: true, disabled: true },
              components: {
                input: ShowFilename,
              },
            }),
            defineField({
              name: 'finish',
              title: 'Finish',
              type: 'string',
              validation: (rule) => rule.required(),
            }),
            defineField({
              name: 'galleryPhoto',
              title: 'Gallery Photo',
              type: 'image',
              options: {
                hotspot: true,
              },
              fields: [
                defineField({
                  name: 'originalfn',
                  title: 'Original Filename',
                  type: 'string',
                  options: { isHighlighted: true, disabled: true },
                  components: {
                    input: ShowFilename,
                  },
                }),
              ],
            }),
          ],
        },
      ],
      group: 'content',
    }),
    defineField({
      name: 'descriptionTabs',
      title: 'Description Tabs',
      type: 'array',
      of: [
        {
          type: 'object',
          fields: [
            defineField({
              name: 'tabTitle',
              title: 'Tab title',
              type: 'string',
              validation: (rule) => rule.required(),
            }),
            defineField({
              title: 'Page blocks',
              name: 'pageBlocks',
              type: 'array',
              of: [...PageBlockTypes],
            }),
          ],
          preview: {
            select: { title: 'tabTitle' },
          },
        },
      ],
      hidden: ({ parent }) => parent?.simple,
      group: 'content',
    }),
    {
      ...getPageInfo({ path: 'sku' }),
      hidden: ({ parent }) => parent?.simple,
    },
  ],
  preview: {
    select: {
      name: 'name',
      sku: 'sku',
      media: 'photo',
    },
    prepare({ name: title = 'New SKU', sku: subtitle = '', media }) {
      return { title, subtitle, media }
    },
  },
})